import React, { useEffect, useMemo, useRef, useState } from 'react';
import api, { getProfileAssetUrl } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { MessageCircle, Users, GraduationCap, ArrowLeft, Shield } from 'lucide-react';

const POLL_MS = 15000;

export default function ChatDropdownContent({ open, onUnreadChanged }) {
  const { user } = useAuth();
  const [tab, setTab] = useState('students');
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const listRef = useRef(null);
  const pollRef = useRef(null);

  const title = tab === 'students' ? 'Students' : tab === 'teachers' ? 'Teachers' : 'Management';

  const fetchContacts = async () => {
    if (!open) return;
    setLoadingContacts(true);
    try {
      const roleParam = tab === 'students' ? 'student' : tab === 'teachers' ? 'teacher' : 'other';
      const res = await api.get('/chat/contacts', { params: { role: roleParam } });
      setContacts(res.data?.data?.items || []);
    } catch {
      setContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  };

  const fetchConversation = async (contact) => {
    if (!contact?.user_id) return;
    setLoadingMessages(true);
    try {
      const res = await api.get(`/chat/conversations/with/${contact.user_id}`);
      setMessages(res.data?.data?.messages || []);
      fetchContacts();
      if (onUnreadChanged) onUnreadChanged();
    } catch {
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchContacts();
    }
  }, [open, tab]);

  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => fetchContacts(), POLL_MS);
    return () => clearInterval(id);
  }, [open, tab]);

  useEffect(() => {
    if (!open || !selectedContact) return;
    fetchConversation(selectedContact);
    pollRef.current = setInterval(() => fetchConversation(selectedContact), POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [open, selectedContact]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    const text = messageText.trim();
    if (!text || !selectedContact) return;
    setMessageText('');
    try {
      const res = await api.post('/chat/messages', {
        recipient_user_id: selectedContact.user_id,
        message: text,
      });
      const newMsg = res.data?.data;
      if (newMsg) {
        setMessages((prev) => [...prev, newMsg]);
      } else {
        fetchConversation(selectedContact);
      }
    } catch {
      setMessageText(text);
    } finally {
      if (onUnreadChanged) onUnreadChanged();
    }
  };

  const contactsList = useMemo(() => {
    const list = contacts.map((c) => ({
      ...c,
      initials: c.name?.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase(),
    }));
    if (!searchTerm.trim()) return list;
    const term = searchTerm.toLowerCase();
    return list.filter((c) =>
      (c.name || '').toLowerCase().includes(term) ||
      (c.subtitle || '').toLowerCase().includes(term)
    );
  }, [contacts, searchTerm]);

  return (
    <div className="w-80 bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
      <div className="px-3 py-2 border-b border-slate-700 flex items-center justify-between">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-700">
            <TabsTrigger value="students" className="text-xs">
              <GraduationCap className="w-3 h-3 mr-1" /> Students
            </TabsTrigger>
            <TabsTrigger value="teachers" className="text-xs">
              <Users className="w-3 h-3 mr-1" /> Teachers
            </TabsTrigger>
            <TabsTrigger value="other" className="text-xs">
              <Shield className="w-3 h-3 mr-1" /> Management
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {!selectedContact ? (
        <div className="max-h-96 overflow-y-auto">
          <div className="px-3 pt-3">
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={`Search ${title.toLowerCase()}...`}
              className="bg-slate-700 border-slate-600 text-white h-8"
            />
          </div>
          {loadingContacts ? (
            <div className="px-3 py-6 text-sm text-slate-400 text-center">Loading {title}...</div>
          ) : contactsList.length === 0 ? (
            <div className="px-3 py-6 text-sm text-slate-400 text-center">No {title} found</div>
          ) : (
            contactsList.map((c) => (
              <button
                key={`${c.contact_type}-${c.contact_id}`}
                type="button"
                onClick={() => setSelectedContact(c)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-700/60 ${
                  c.unread_count > 0 ? 'bg-slate-700/40' : ''
                }`}
              >
                <Avatar className="h-8 w-8">
                  {c.avatar ? (
                    <AvatarImage src={getProfileAssetUrl(c.avatar)} alt={c.name} />
                  ) : null}
                  <AvatarFallback className="bg-slate-700 text-white text-xs">
                    {c.initials || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{c.name || 'User'}</div>
                  {c.subtitle && <div className="text-[11px] text-slate-400 truncate">{c.subtitle}</div>}
                </div>
                {c.unread_count > 0 && (
                  <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
                    {c.unread_count > 9 ? '9+' : c.unread_count}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      ) : (
        <div className="flex flex-col h-96">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-700">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-slate-300 hover:text-white"
              onClick={() => { setSelectedContact(null); setMessages([]); }}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="text-sm text-white font-medium">{selectedContact.name}</div>
          </div>
          <div ref={listRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
            {loadingMessages ? (
              <div className="text-sm text-slate-400 text-center">Loading messages...</div>
            ) : messages.length === 0 ? (
              <div className="text-sm text-slate-400 text-center">Start the conversation</div>
            ) : (
              messages.map((m) => {
                const mine = String(m.sender_id) === String(user?.id);
                return (
                  <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] px-3 py-2 rounded-lg text-sm ${mine ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-100'}`}>
                      {m.body}
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <form onSubmit={handleSend} className="p-3 border-t border-slate-700 flex gap-2">
            <Input
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder="Type a message..."
              className="bg-slate-700 border-slate-600 text-white"
            />
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700">Send</Button>
          </form>
        </div>
      )}
    </div>
  );
}
